/**
 * Shared marketing-surface theme resolver (landing + legal pages).
 *
 * The marketing CSS is DARK by default; light styling only applies under
 * `[data-dc-theme="light"]`. The landing page persists the user's chosen mode
 * under `dc-marketing-theme`; the legal pages (Privacy/Terms) must respect that
 * same choice instead of being permanently dark. Centralising the read +
 * system-preference tracking + resolution here keeps the three pages in lockstep
 * (previously the legal pages had no theme wiring at all → always dark).
 *
 * Scoped to the marketing surface only — independent of the app's own theme.
 */
import { useCallback, useEffect, useState } from 'react';

/** `system` follows the OS `prefers-color-scheme`. */
export type MarketingThemeMode = 'system' | 'light' | 'dark';

/** Marketing-scoped persistence key (independent of the app's theme). */
export const MARKETING_THEME_KEY = 'dc-marketing-theme';

/** Read the persisted mode; default `system` (also when storage is blocked). */
function readStoredThemeMode(): MarketingThemeMode {
  try {
    const v = window.localStorage.getItem(MARKETING_THEME_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / storage blocked — fall through to default */
  }
  return 'system';
}

/** Does the OS currently prefer dark? (Defaults to dark when matchMedia absent.) */
function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export interface MarketingTheme {
  /** The user's chosen MODE (persisted). */
  mode: MarketingThemeMode;
  /** Persist + apply a new mode. */
  setMode: (mode: MarketingThemeMode) => void;
  /** The concrete `light`|`dark` to put on `data-dc-theme`. */
  resolvedTheme: 'light' | 'dark';
}

export function useMarketingTheme(): MarketingTheme {
  const [mode, setModeState] = useState<MarketingThemeMode>(() => readStoredThemeMode());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Track the OS preference live (for `system` mode).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Legacy Safari < 14 / older engines: addEventListener absent on MQL.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  // Cross-page / cross-tab sync: pick up a mode chosen on another marketing page
  // (e.g. the landing toggle) without a full reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent): void => {
      if (e.key === MARKETING_THEME_KEY) setModeState(readStoredThemeMode());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: MarketingThemeMode): void => {
    setModeState(next);
    try {
      window.localStorage.setItem(MARKETING_THEME_KEY, next);
      // Mirror the choice into the logged-in app's theme key so the app
      // adopts the same theme the user picked on the landing page. Both use
      // the identical 'dark' | 'light' | 'system' shape (see src/lib/theme.ts
      // STORAGE_KEY = 'da-theme'); the app reads it on boot via
      // getStoredThemePreference(), so a change here carries into the app.
      window.localStorage.setItem('da-theme', next);
    } catch {
      /* storage blocked — in-memory only for this session */
    }
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  return { mode, setMode, resolvedTheme };
}
